/**
 * Preset library: scenes saved by name, 10 banks × 10 slots (hotkeys 1–0), an unlimited
 * favorites list, custom palettes and custom (ISF) visualizations. Stored in IndexedDB;
 * import/export as JSON documents.
 */
import { newScene, sanitizeScene, type Scene } from '../shared/scene';
import type { AsyncKV } from './storage';

export const BANKS = 10;
export const SLOTS = 10;

export interface Preset {
  id: string;
  name: string;
  scene: Scene;
  /** Small JPEG data URL. */
  thumbnail?: string;
  builtIn?: boolean;
  /** For built-ins added in an update: the name of the bank they're placed in. */
  bankName?: string;
  created: number;
  updated: number;
}

export interface CustomPalette {
  id: string;
  name: string;
  stops: string[];
  cyclic?: boolean;
}

export interface CustomVisualization {
  id: string;
  name: string;
  /** ISF fragment shader source (with JSON header). */
  source: string;
  created: number;
}

interface Meta {
  version: 1;
  banks: (string | null)[][];
  bankNames: string[];
  seeded: boolean;
  /** Favorite preset ids, newest first. Unlimited and independent of banks. */
  favorites: string[];
  /** Built-in preset ids already offered (so later additions arrive once). */
  builtInsSeen?: string[];
}

const emptyMeta = (): Meta => ({
  version: 1,
  banks: Array.from({ length: BANKS }, () => Array<string | null>(SLOTS).fill(null)),
  bankNames: Array.from({ length: BANKS }, (_, i) => `Bank ${i + 1}`),
  seeded: false,
  favorites: [],
});

export type LibraryDocument =
  | { format: 'glowtrope-preset'; version: 1; preset: { name: string; scene: unknown } }
  | {
      format: 'glowtrope-bank';
      version: 1;
      name: string;
      slots: ({ name: string; scene: unknown } | null)[];
    }
  | {
      format: 'glowtrope-library';
      version: 1;
      presets: { id: string; name: string; scene: unknown }[];
      banks: (string | null)[][];
      bankNames: string[];
      palettes: CustomPalette[];
      visualizations: CustomVisualization[];
      favorites?: string[];
    };

const newId = (p: string) =>
  `${p}${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export class PresetLibrary {
  private presets = new Map<string, Preset>();
  private palettes = new Map<string, CustomPalette>();
  private visualizations = new Map<string, CustomVisualization>();
  private meta: Meta = emptyMeta();
  private readonly listeners = new Set<() => void>();

  constructor(
    private readonly kv: AsyncKV,
    private readonly seed: () => Preset[] = () => [],
  ) {}

  async load(): Promise<void> {
    const all = await this.kv.entries<unknown>();
    for (const [key, value] of all) {
      if (key === 'meta') this.meta = this.cleanMeta(value);
      else if (key.startsWith('preset:')) {
        const p = this.cleanPreset(value);
        if (p) this.presets.set(p.id, p);
      } else if (key.startsWith('palette:')) {
        const p = value as CustomPalette;
        if (p && typeof p.id === 'string' && Array.isArray(p.stops)) this.palettes.set(p.id, p);
      } else if (key.startsWith('viz:')) {
        const v = value as CustomVisualization;
        if (v && typeof v.id === 'string' && typeof v.source === 'string')
          this.visualizations.set(v.id, v);
      }
    }
    const builtIns = this.seed();
    if (!this.meta.seeded) {
      // Bank 1 gets the general built-ins; presets that name their own bank (like Concert) get
      // the next banks, one per name.
      const named = new Map<string, number>();
      let slot0 = 0;
      for (const p of builtIns) {
        this.presets.set(p.id, p);
        if (!p.bankName) {
          if (slot0 < SLOTS) this.meta.banks[0]![slot0++] = p.id;
          continue;
        }
        if (!named.has(p.bankName)) {
          const b = 1 + named.size;
          if (b >= BANKS) continue;
          named.set(p.bankName, b);
          this.meta.bankNames[b] = p.bankName;
        }
        const bank = this.meta.banks[named.get(p.bankName)!]!;
        const free = bank.indexOf(null);
        if (free >= 0) bank[free] = p.id;
      }
      this.meta.bankNames[0] = 'Built-in';
      this.meta.seeded = true;
      this.meta.builtInsSeen = builtIns.map((p) => p.id);
      await Promise.all([
        ...builtIns.map((p) => this.kv.set(`preset:${p.id}`, p)),
        this.saveMeta(),
      ]);
    } else {
      // Built-ins added in later versions arrive once (deleting one doesn't bring it back).
      // Libraries from before this was tracked count the first bank's worth as seen.
      const seen = new Set(this.meta.builtInsSeen ?? builtIns.slice(0, SLOTS).map((p) => p.id));
      const fresh = builtIns.filter((p) => !seen.has(p.id));
      if (fresh.length || !this.meta.builtInsSeen) {
        const empty = this.meta.banks.findIndex((b, i) => i > 0 && b.every((id) => id === null));
        fresh.forEach((p, i) => {
          this.presets.set(p.id, p);
          if (empty >= 0 && i < SLOTS) this.meta.banks[empty]![i] = p.id;
        });
        if (empty >= 0 && fresh.length && fresh[0]!.bankName)
          this.meta.bankNames[empty] = fresh[0]!.bankName;
        this.meta.builtInsSeen = [...seen, ...fresh.map((p) => p.id)];
        await Promise.all([...fresh.map((p) => this.kv.set(`preset:${p.id}`, p)), this.saveMeta()]);
      }
    }
    this.emit();
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  list(): Preset[] {
    return [...this.presets.values()].sort((a, b) => a.name.localeCompare(b.name));
  }

  get(id: string): Preset | undefined {
    return this.presets.get(id);
  }

  bankName(bank: number): string {
    return this.meta.bankNames[bank] ?? `Bank ${bank + 1}`;
  }

  bank(bank: number): (Preset | null)[] {
    return (this.meta.banks[bank] ?? []).map((id) => (id ? (this.presets.get(id) ?? null) : null));
  }

  presetAt(bank: number, slot: number): Preset | null {
    const id = this.meta.banks[bank]?.[slot];
    return id ? (this.presets.get(id) ?? null) : null;
  }

  /** Presets in a bank, in slot order, skipping empty slots. */
  bankPresets(bank: number): Preset[] {
    return this.bank(bank).filter((p): p is Preset => p !== null);
  }

  async save(input: {
    id?: string;
    name: string;
    scene: Scene;
    thumbnail?: string;
  }): Promise<Preset> {
    const now = Date.now();
    const existing = input.id ? this.presets.get(input.id) : undefined;
    const preset: Preset = {
      id: existing?.id ?? newId('p'),
      name: input.name.slice(0, 80) || 'Untitled',
      scene: { ...input.scene, name: input.name.slice(0, 80) || 'Untitled' },
      created: existing?.created ?? now,
      updated: now,
    };
    const thumb = input.thumbnail ?? existing?.thumbnail;
    if (thumb) preset.thumbnail = thumb;
    this.presets.set(preset.id, preset);
    await this.kv.set(`preset:${preset.id}`, preset);
    this.emit();
    return preset;
  }

  /** Attach a thumbnail without touching anything else (works for built-ins too). */
  async setThumbnail(id: string, thumbnail: string): Promise<void> {
    const p = this.presets.get(id);
    if (!p) return;
    const next = { ...p, thumbnail };
    this.presets.set(id, next);
    await this.kv.set(`preset:${id}`, next);
    this.emit();
  }

  async rename(id: string, name: string): Promise<void> {
    const p = this.presets.get(id);
    if (p) await this.save({ id, name, scene: p.scene });
  }

  async remove(id: string): Promise<void> {
    this.presets.delete(id);
    this.meta.favorites = this.meta.favorites.filter((f) => f !== id);
    for (const bank of this.meta.banks)
      for (let s = 0; s < bank.length; s++) if (bank[s] === id) bank[s] = null;
    await Promise.all([this.kv.delete(`preset:${id}`), this.saveMeta()]);
    this.emit();
  }

  async assign(bank: number, slot: number, presetId: string | null): Promise<void> {
    const b = this.meta.banks[bank];
    if (!b || slot < 0 || slot >= SLOTS) return;
    b[slot] = presetId;
    await this.saveMeta();
    this.emit();
  }

  /** Save the scene as a new preset in a slot (Shift+number). */
  async storeToSlot(bank: number, slot: number, scene: Scene, thumbnail?: string): Promise<Preset> {
    const name =
      scene.name && scene.name !== 'Untitled'
        ? scene.name
        : `${this.bankName(bank)} · ${slot === 9 ? 0 : slot + 1}`;
    const p = await this.save({ name, scene, ...(thumbnail ? { thumbnail } : {}) });
    await this.assign(bank, slot, p.id);
    return p;
  }

  // ---- favorites

  favorites(): Preset[] {
    return this.meta.favorites
      .map((id) => this.presets.get(id))
      .filter((p): p is Preset => p !== undefined);
  }

  isFavorite(id: string | null | undefined): boolean {
    return !!id && this.meta.favorites.includes(id);
  }

  async setFavorite(id: string, on: boolean): Promise<void> {
    if (!this.presets.has(id) || this.isFavorite(id) === on) return;
    this.meta.favorites = on
      ? [id, ...this.meta.favorites]
      : this.meta.favorites.filter((f) => f !== id);
    await this.saveMeta();
    this.emit();
  }

  /** Save a scene straight into favorites (as a library preset that is in no bank). */
  async addFavorite(scene: Scene, name: string, thumbnail?: string): Promise<Preset> {
    const p = await this.save({ name, scene, ...(thumbnail ? { thumbnail } : {}) });
    await this.setFavorite(p.id, true);
    return p;
  }

  async renameBank(bank: number, name: string): Promise<void> {
    if (bank < 0 || bank >= BANKS) return;
    this.meta.bankNames[bank] = name.slice(0, 40) || `Bank ${bank + 1}`;
    await this.saveMeta();
    this.emit();
  }

  // ---- custom palettes & visualizations

  listPalettes(): CustomPalette[] {
    return [...this.palettes.values()];
  }

  async savePalette(p: Omit<CustomPalette, 'id'> & { id?: string }): Promise<CustomPalette> {
    const palette: CustomPalette = { ...p, id: p.id ?? newId('custom-') };
    this.palettes.set(palette.id, palette);
    await this.kv.set(`palette:${palette.id}`, palette);
    this.emit();
    return palette;
  }

  async removePalette(id: string): Promise<void> {
    this.palettes.delete(id);
    await this.kv.delete(`palette:${id}`);
    this.emit();
  }

  listVisualizations(): CustomVisualization[] {
    return [...this.visualizations.values()];
  }

  async saveVisualization(v: Omit<CustomVisualization, 'created'>): Promise<CustomVisualization> {
    const viz: CustomVisualization = { ...v, created: Date.now() };
    this.visualizations.set(viz.id, viz);
    await this.kv.set(`viz:${viz.id}`, viz);
    this.emit();
    return viz;
  }

  async removeVisualization(id: string): Promise<void> {
    this.visualizations.delete(id);
    await this.kv.delete(`viz:${id}`);
    this.emit();
  }

  // ---- import / export

  exportPreset(id: string): LibraryDocument | null {
    const p = this.presets.get(id);
    return p
      ? { format: 'glowtrope-preset', version: 1, preset: { name: p.name, scene: p.scene } }
      : null;
  }

  exportBank(bank: number): LibraryDocument {
    return {
      format: 'glowtrope-bank',
      version: 1,
      name: this.bankName(bank),
      slots: this.bank(bank).map((p) => (p ? { name: p.name, scene: p.scene } : null)),
    };
  }

  exportLibrary(): LibraryDocument {
    return {
      format: 'glowtrope-library',
      version: 1,
      presets: this.list().map((p) => ({ id: p.id, name: p.name, scene: p.scene })),
      banks: this.meta.banks.map((b) => [...b]),
      bankNames: [...this.meta.bankNames],
      palettes: this.listPalettes(),
      visualizations: this.listVisualizations(),
      favorites: [...this.meta.favorites],
    };
  }

  /**
   * Import a document. Presets get new ids so imports never overwrite. Banks import into
   * `targetBank`. Returns the number of presets added.
   */
  async importDocument(doc: unknown, targetBank = 1): Promise<number> {
    if (!doc || typeof doc !== 'object') throw new Error('Not a preset file');
    const d = doc as Record<string, unknown>;
    const fallback = newScene();
    const add = async (name: unknown, scene: unknown) => {
      const s = sanitizeScene(scene, fallback);
      if (s === fallback) return null;
      return this.save({ name: typeof name === 'string' ? name : s.name, scene: s });
    };
    if (d.format === 'glowtrope-preset') {
      const p = d.preset as Record<string, unknown> | undefined;
      return (await add(p?.name, p?.scene)) ? 1 : 0;
    }
    if (d.format === 'glowtrope-bank' && Array.isArray(d.slots)) {
      let n = 0;
      for (let s = 0; s < Math.min(SLOTS, d.slots.length); s++) {
        const slot = d.slots[s] as Record<string, unknown> | null;
        if (!slot) continue;
        const p = await add(slot.name, slot.scene);
        if (p) {
          n++;
          this.meta.banks[targetBank]![s] = p.id;
        }
      }
      if (typeof d.name === 'string') this.meta.bankNames[targetBank] = d.name.slice(0, 40);
      await this.saveMeta();
      this.emit();
      return n;
    }
    if (d.format === 'glowtrope-library' && Array.isArray(d.presets)) {
      const idMap = new Map<string, string>();
      for (const raw of d.presets as Record<string, unknown>[]) {
        const p = await add(raw?.name, raw?.scene);
        if (p && typeof raw.id === 'string') idMap.set(raw.id, p.id);
      }
      if (Array.isArray(d.banks)) {
        (d.banks as unknown[][]).slice(0, BANKS).forEach((bank, b) => {
          if (!Array.isArray(bank)) return;
          bank.slice(0, SLOTS).forEach((id, s) => {
            if (typeof id === 'string' && idMap.has(id)) this.meta.banks[b]![s] = idMap.get(id)!;
          });
        });
      }
      if (Array.isArray(d.favorites)) {
        const imported = (d.favorites as unknown[])
          .map((id) => (typeof id === 'string' ? idMap.get(id) : undefined))
          .filter((id): id is string => !!id && !this.meta.favorites.includes(id));
        this.meta.favorites = [...imported, ...this.meta.favorites];
      }
      for (const p of (d.palettes as CustomPalette[] | undefined) ?? []) {
        if (p && Array.isArray(p.stops)) await this.savePalette(p);
      }
      for (const v of (d.visualizations as CustomVisualization[] | undefined) ?? []) {
        if (v && typeof v.source === 'string')
          await this.saveVisualization({ id: v.id, name: v.name, source: v.source });
      }
      await this.saveMeta();
      this.emit();
      return idMap.size;
    }
    if (d.schemaVersion !== undefined) return (await add(d.name, d)) ? 1 : 0;
    throw new Error('Unrecognized file format');
  }

  private cleanPreset(v: unknown): Preset | null {
    if (!v || typeof v !== 'object') return null;
    const o = v as Record<string, unknown>;
    if (typeof o.id !== 'string') return null;
    const fallback = newScene();
    const scene = sanitizeScene(o.scene, fallback);
    if (scene === fallback) return null;
    const p: Preset = {
      id: o.id,
      name: typeof o.name === 'string' ? o.name : scene.name,
      scene,
      created: typeof o.created === 'number' ? o.created : 0,
      updated: typeof o.updated === 'number' ? o.updated : 0,
    };
    if (typeof o.thumbnail === 'string' && o.thumbnail.startsWith('data:image/'))
      p.thumbnail = o.thumbnail;
    if (o.builtIn === true) p.builtIn = true;
    return p;
  }

  private cleanMeta(v: unknown): Meta {
    const m = emptyMeta();
    if (!v || typeof v !== 'object') return m;
    const o = v as Record<string, unknown>;
    if (Array.isArray(o.banks)) {
      (o.banks as unknown[]).slice(0, BANKS).forEach((bank, b) => {
        if (Array.isArray(bank))
          bank
            .slice(0, SLOTS)
            .forEach((id, s) => (m.banks[b]![s] = typeof id === 'string' ? id : null));
      });
    }
    if (Array.isArray(o.bankNames)) {
      (o.bankNames as unknown[])
        .slice(0, BANKS)
        .forEach((n, i) => typeof n === 'string' && (m.bankNames[i] = n));
    }
    m.seeded = o.seeded === true;
    if (Array.isArray(o.builtInsSeen))
      m.builtInsSeen = (o.builtInsSeen as unknown[]).filter(
        (x): x is string => typeof x === 'string',
      );
    if (Array.isArray(o.favorites))
      m.favorites = [...new Set((o.favorites as unknown[]).filter((x) => typeof x === 'string'))];
    return m;
  }

  private saveMeta(): Promise<void> {
    return this.kv.set('meta', this.meta);
  }

  private emit(): void {
    for (const l of this.listeners) l();
  }
}
